import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Who at Connexis looks after this partner.
 *
 * A lender only ever sees the Connexis person named here — that is the whole point of the rule in
 * `hub/partner_contact.py`. An introducer with a recruiter keeps seeing their recruiter as the main
 * card; this manager becomes their Connexis route.
 */
export function AssignManagerDialog({ open, onOpenChange, kind, party, onDone }) {
  const [managers, setManagers] = useState(null);
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(party?.account_manager_id || null);
    api.get("/hub/managers").then((r) => setManagers(r.data.items || [])).catch(() => setManagers([]));
  }, [open, party]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/hub/partners/${kind}/${party.id}/manager`, { manager_id: picked });
      toast.success(r.data.account_manager_name
        ? `${r.data.account_manager_name} now looks after ${party.company_name || party.name}`
        : `${party.company_name || party.name} is back on the house contact`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
    } finally { setSaving(false); }
  };

  const Opt = ({ id, title, sub, testId }) => (
    <label className={`opt${picked === id ? " pick" : ""}`} data-testid={testId}
           style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
      <input type="radio" checked={picked === id} onChange={() => setPicked(id)}
             style={{ accentColor: "var(--accent)", width: 15, height: 15, marginTop: 3 }} />
      <div style={{ minWidth: 0 }}>
        <div className="cell-main">{title}</div>
        {sub && <div className="cell-sub">{sub}</div>}
      </div>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="assign-manager-dialog">
        <DialogHeader>
          <DialogTitle>Who looks after {party?.company_name || party?.name}?</DialogTitle>
          <DialogDescription>
            {kind === "lender"
              ? "This is the only Connexis contact the desk ever sees — name, mobile and WhatsApp."
              : "Their Connexis route. An introducer brought on by another introducer still sees their recruiter first."}
          </DialogDescription>
        </DialogHeader>

        <div className="cxp">
          {managers === null ? (
            <div className="cell-sub">Reading the manager list…</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 9, maxHeight: 360, overflowY: "auto" }}>
              <Opt id={null} title="The house contact" testId="assign-manager-house"
                   sub="Whoever is set under “Who your partners contact”" />
              {managers.filter((m) => m.active !== false).map((m) => (
                <Opt key={m.id} id={m.id} title={m.name} testId={`assign-manager-${m.id}`}
                     sub={[m.title, m.phone, m.email].filter(Boolean).join(" · ") || "No details on file yet"} />
              ))}
              {managers.length === 0 && (
                <div className="cell-sub" data-testid="assign-manager-empty">
                  No managers added yet — add them under <b>What reaches you</b>.
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button className="btn btn-ghost" onClick={() => onOpenChange(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="assign-manager-save">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
